import React, { ReactNode, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { useStore } from '@src/models';
import { AccordionMenu as AccordionMenuComponent, Button, Flex } from 'src/kit';
import { Loader } from 'src/components';
import { Filter } from './Filter';

const FilterWrapper = styled.div<{ stickyTop: number }>`
  position: sticky;
  top: ${props => props.stickyTop}px;

  @media (max-width: 1024px) {
    position: static;
  }
`;

const MobileFilterButton = styled(Button)`
  display: none;
  width: 100%;
  margin-bottom: 16px;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-weight: 600;
    font-size: 15px;
    background: #fff;
    border: 1px solid #e5e5e5;
    color: #333;
    transition: all 0.2s ease;
    margin-top: 12.5px;

    &:hover {
      border-color: #22c993;
      background: #f9f9f9;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const FilterOverlay = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 1024px) {
    display: ${props => (props.isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    animation: ${props => (props.isOpen ? 'fadeIn' : 'fadeOut')} 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const FilterPanel = styled.div<{ isOpen: boolean }>`
  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    background: white;
    z-index: 10000;
    overflow-y: auto;
    padding: 20px;
    transform: ${props => (props.isOpen ? 'translateY(0)' : 'translateY(-100%)')};
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const DesktopFilters = styled.div`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const MobileFilterHeader = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 2px solid #e5e5e5;
  }
`;

const MobileFilterTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const CloseButton = styled.button`
  background: #f5f5f5;
  border: none;
  font-size: 28px;
  cursor: pointer;
  padding: 8px 12px;
  color: #666;
  border-radius: 8px;
  line-height: 1;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #e5e5e5;
    color: #000;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MobileFilterFooter = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
    position: sticky;
    bottom: 0;
    background: white;
    padding: 20px 0;
    margin-top: 24px;
    border-top: 2px solid #e5e5e5;
  }
`;

const CloseFooterButton = styled(Button)`
  width: 100%;
  padding: 14px 24px;
  font-weight: 600;
  font-size: 16px;
  background: #22c993;
  color: white;
  border: none;
  transition: all 0.2s ease;

  &:hover {
    background: #1db584;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 201, 147, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FilterIcon = styled.span`
  display: inline-block;
  font-size: 18px;
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
  margin-left: 4px;
`;

export const Filters = observer(function({
  generateFilters,
  stickyTop = 60,
  loading = false,
  beforeOnClearAll,
  children = null,
  storeName = 'feed'
}: {
  generateFilters: (data: any) => Array<{ id: any; label: string; items: Array<string> }>;
  stickyTop?: number;
  loading?: boolean;
  children?: ReactNode;
  storeName?: string;
  beforeOnClearAll?: () => void;
}) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const {
    [storeName]: {
      jobs: { filters: data, setFilter, checkIsFiltersOn, isFetching, clearFilters }
    }
  } = useStore();

  const filters = generateFilters(data);

  if (loading) {
    return <Loader css={{ position: 'relative', height: '200px' }} />;
  }

  const handleClearAll = () => {
    if (typeof beforeOnClearAll === 'function') beforeOnClearAll();
    clearFilters();
  };

  const handleClose = () => {
    setIsFilterOpen(false);
  };

  const filterContent = (
    <>
      {filters.map((filter, index) => (
        <Filter setFilter={setFilter} key={filter.label} disabled={isFetching} index={index} {...filter} />
      ))}
      {children}
      {checkIsFiltersOn() && filters.length > 0 && (
        <Flex justifyContent="flex-end" mt={20}>
          <Button
            onClick={handleClearAll}
            size="sm"
            variant="secondary"
            style={{
              backgroundColor: '#22C993',
              color: 'white',
              border: 'none'
            }}
          >
            Clear All
          </Button>
        </Flex>
      )}
    </>
  );

  return (
    <>
      <MobileFilterButton
        onClick={() => setIsFilterOpen(true)}
        variant="secondary"
        style={{
          borderColor: '#22C993',
          color: '#22C993'
        }}
      >
        <FilterIcon>⚙️</FilterIcon>
        Advanced Filters
        {checkIsFiltersOn() && <FilterBadge />}
      </MobileFilterButton>

      <FilterOverlay isOpen={isFilterOpen} onClick={handleClose} />
      <FilterPanel isOpen={isFilterOpen}>
        <MobileFilterHeader>
          <MobileFilterTitle>Filters</MobileFilterTitle>
          <CloseButton onClick={handleClose} aria-label="Fermer">
            ×
          </CloseButton>
        </MobileFilterHeader>

        <AccordionMenuComponent style={{ position: 'static' }}>{filterContent}</AccordionMenuComponent>

        <MobileFilterFooter>
          <CloseFooterButton onClick={handleClose} variant="primary">
            {checkIsFiltersOn() ? 'See results' : 'Close'}
          </CloseFooterButton>
        </MobileFilterFooter>
      </FilterPanel>
    </>
  );
});
